import React, { useState, useEffect } from 'react';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useStore } from '../context/StoreContext';
import { addDays, format, parseISO } from 'date-fns';

const { FiX, FiClock, FiZap, FiCalendar } = FiIcons;

const ScheduleModal = ({ isOpen, onClose, onSave, properties }) => {
  const { data } = useStore();
  const [formData, setFormData] = useState({
    propertyId: '',
    templateId: '',
    scheduledDate: '',
    type: 'Email'
  });

  const [triggerType, setTriggerType] = useState('date'); // 'date' or 'relative'

  useEffect(() => {
    if (!isOpen) {
      setFormData({ propertyId: '', templateId: '', scheduledDate: '', type: 'Email' });
      setTriggerType('date');
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleRelativeTrigger = (type, offset) => {
    const property = properties.find(p => p.id === formData.propertyId);
    if (!property) return;

    let baseDate = type === 'move-in' ? property.leaseStart : property.leaseEnd;
    if (!baseDate) return;

    const newDate = addDays(parseISO(baseDate), offset);
    setFormData(prev => ({ ...prev, scheduledDate: format(newDate, 'yyyy-MM-dd') }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const template = data.communicationTemplates.find(t => t.id === formData.templateId);
    const prop = properties.find(p => p.id === formData.propertyId);
    
    onSave({
      ...formData,
      status: 'Pending',
      subject: template?.subject.replace('[Property Name]', prop?.name || '') || 'Scheduled Message'
    });
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-in zoom-in duration-200">
        <div className="flex justify-between items-center p-6 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <div className="p-2 bg-amber-100 text-amber-600 rounded-lg">
              <SafeIcon icon={FiZap} />
            </div>
            <h2 className="text-xl font-bold text-gray-800">Schedule Automation</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <SafeIcon icon={FiX} size={24} />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">Target Property</label>
            <select 
              required 
              className="w-full p-3 border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-amber-500 bg-gray-50 text-sm"
              value={formData.propertyId}
              onChange={e => setFormData({...formData, propertyId: e.target.value})}
            >
              <option value="">Select Property...</option>
              {properties.map(p => (
                <option key={p.id} value={p.id}>{p.name} ({p.tenantName || 'Vacant'})</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">Message Template</label>
            <select 
              required
              className="w-full p-3 border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-amber-500 bg-gray-50 text-sm"
              value={formData.templateId}
              onChange={e => setFormData({...formData, templateId: e.target.value})}
            >
              <option value="">Select Template...</option>
              {data.communicationTemplates.map(t => (
                <option key={t.id} value={t.id}>{t.title}</option>
              ))}
            </select>
          </div>

          <div className="space-y-3 pt-2">
            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider">Trigger Logic</label>
            <div className="flex bg-gray-100 p-1 rounded-xl">
              <button 
                type="button"
                onClick={() => setTriggerType('date')}
                className={`flex-1 py-2 text-xs font-bold rounded-lg transition-all ${triggerType === 'date' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'}`}
              >
                Fixed Date
              </button>
              <button 
                type="button"
                disabled={!formData.propertyId}
                onClick={() => setTriggerType('relative')}
                className={`flex-1 py-2 text-xs font-bold rounded-lg transition-all disabled:opacity-30 ${triggerType === 'relative' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'}`}
              >
                Lease Relative
              </button>
            </div>

            {triggerType === 'date' ? (
              <input 
                required 
                type="date" 
                className="w-full p-3 border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-amber-500 bg-gray-50 text-sm"
                value={formData.scheduledDate}
                onChange={e => setFormData({...formData, scheduledDate: e.target.value})}
              />
            ) : (
              <div className="grid grid-cols-2 gap-2">
                <button type="button" onClick={() => handleRelativeTrigger('move-in', 2)} className="p-2 text-[10px] font-bold bg-amber-50 text-amber-700 rounded-lg border border-amber-100 hover:bg-amber-100 transition">2 Days After Move-in</button>
                <button type="button" onClick={() => handleRelativeTrigger('move-out', -3)} className="p-2 text-[10px] font-bold bg-amber-50 text-amber-700 rounded-lg border border-amber-100 hover:bg-amber-100 transition">3 Days Before Move-out</button>
              </div>
            )}
            
            {formData.scheduledDate && triggerType === 'relative' && (
              <p className="text-[10px] text-amber-600 font-bold text-center">
                Scheduled for: {format(parseISO(formData.scheduledDate), 'MMM dd, yyyy')}
              </p>
            )}
          </div>

          <div className="pt-4 flex gap-3">
            <button type="button" onClick={onClose} className="flex-1 py-3 border border-gray-200 text-gray-600 font-bold rounded-xl hover:bg-gray-50 transition-colors">
              Cancel
            </button>
            <button type="submit" className="flex-1 py-3 bg-amber-600 text-white font-bold rounded-xl hover:bg-amber-700 transition-colors shadow-lg shadow-amber-200">
              Set Automation
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ScheduleModal;